import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import {
  Modal, Stack, Select, Textarea, TextInput, NumberInput,
  Group, Button, Box, Text, SimpleGrid, Grid, Image,
  ActionIcon, FileButton, Divider, Badge, Anchor,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconPlus, IconX, IconUpload, IconTool, IconCalendar, IconVideo } from '@tabler/icons-react'
import SmartDateInput from '../SmartDateInput'
import { createRepairJob, uploadRepairImages } from '../../api/client'

// ── Video URL helpers ────────────────────────────────────────────────────────
function getVideoEmbed(url: string): string | null {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`
  const gd = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/)
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`
  const vm = url.match(/vimeo\.com\/(\d+)/)
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`
  return null
}

function getVideoLabel(url: string) {
  if (url.includes('youtube') || url.includes('youtu.be')) return '▶️ YouTube'
  if (url.includes('drive.google')) return '📁 Google Drive'
  if (url.includes('vimeo')) return '🎬 Vimeo'
  return '🔗 Video'
}

interface Props {
  opened: boolean
  onClose: () => void
  onSuccess: (job?: any) => void
  repairAssets: any[]
  preselectedAssetId?: number | null
  vendors: any[]
  consumableItems: any[]
  allAssets: any[]
  mode?: 'simple' | 'full'   // simple = from Assets page, full = from Repair page
}

export default function RepairJobModal({
  opened, onClose, onSuccess,
  repairAssets, preselectedAssetId, vendors, consumableItems, allAssets,
  mode = 'full',
}: Props) {
  const { t } = useTranslation()
  const isSimple = mode === 'simple'

  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(
    preselectedAssetId ? String(preselectedAssetId) : null
  )
  useEffect(() => {
    if (preselectedAssetId) setSelectedAssetId(String(preselectedAssetId))
  }, [preselectedAssetId])

  const [jobType, setJobType] = useState('INTERNAL')
  const [internalRepairType, setInternalRepairType] = useState('GENERAL')
  const [consumableUsed, setConsumableUsed] = useState<{ id: number; name: string; qty: number }[]>([])
  const [assetUsedId, setAssetUsedId] = useState<string | null>(null)
  const [form, setForm] = useState({
    problem: '', vendorName: '', sentDate: '', expectedReturnDate: '',
    estimatedCost: 0, note: '',
    technicianName: '', technicianContact: '', serviceDate: '',
    serviceLocation: '', serviceContractNo: '',
  })
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [videoUrls, setVideoUrls] = useState<string[]>([])
  const [videoInput, setVideoInput] = useState('')
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setSelectedAssetId(preselectedAssetId ? String(preselectedAssetId) : null)
    setJobType('EXTERNAL'); setInternalRepairType('GENERAL')
    setConsumableUsed([]); setAssetUsedId(null)
    setForm({ problem: '', vendorName: '', sentDate: '', expectedReturnDate: '',
      estimatedCost: 0, note: '', technicianName: '', technicianContact: '',
      serviceDate: '', serviceLocation: '', serviceContractNo: '' })
    setImageFiles([]); setImagePreviews([])
    setVideoUrls([]); setVideoInput('')
  }

  const addImages = (files: File[]) => {
    setImageFiles(p => [...p, ...files])
    files.forEach(f => {
      const r = new FileReader()
      r.onload = e => setImagePreviews(p => [...p, e.target?.result as string])
      r.readAsDataURL(f)
    })
  }

  const handleCreate = async () => {
    if (!selectedAssetId) { notifications.show({ color: 'red', message: t('repair.selectAssetError', 'Please select an Asset') }); return }
    if (!form.problem) { notifications.show({ color: 'red', message: t('repair.describeProblemError', 'Please describe the problem') }); return }
    if (isSimple && !form.sentDate) { notifications.show({ color: 'red', message: t('repair.enterSendDateError', 'Please enter the send date') }); return }
    setSaving(true)
    try {
      let noteExtra = form.note
      if (!isSimple && jobType === 'INTERNAL') {
        const parts = [`Sub-type: ${internalRepairType}`]
        if (internalRepairType === 'USE_ASSET' && assetUsedId) {
          const a = allAssets.find((x: any) => String(x.id) === assetUsedId)
          parts.push(`Used Asset: ${a?.assetCode ?? assetUsedId}`)
        }
        if (internalRepairType === 'USE_CONSUMABLE' && consumableUsed.length > 0) {
          parts.push(`Consumables: ${consumableUsed.map(c => `${c.name} x${c.qty}`).join(', ')}`)
        }
        noteExtra = parts.join(' | ') + (form.note ? ` | ${form.note}` : '')
      }
      const res = await createRepairJob({
        assetId: parseInt(selectedAssetId),
        type: isSimple ? 'EXTERNAL' : jobType,
        problem: form.problem,
        ...(form.vendorName ? { vendorName: form.vendorName } : {}),
        ...(form.sentDate ? { sentDate: form.sentDate } : {}),
        ...(form.expectedReturnDate ? { expectedReturnDate: form.expectedReturnDate } : {}),
        ...(form.estimatedCost ? { estimatedCost: form.estimatedCost } : {}),
        ...(noteExtra ? { note: noteExtra } : {}),
        ...(!isSimple && jobType === 'ON_SITE' ? {
          technicianName: form.technicianName || undefined,
          technicianContact: form.technicianContact || undefined,
          serviceDate: form.serviceDate || undefined,
          serviceLocation: form.serviceLocation || undefined,
          serviceContractNo: form.serviceContractNo || undefined,
        } : {}),
        ...(!isSimple && jobType === 'INTERNAL' && internalRepairType === 'USE_CONSUMABLE' ? { consumableUsed } : {}),
        ...(videoUrls.length > 0 ? { videoUrls } : {}),
      })
      if (imageFiles.length > 0) {
        const fd = new FormData()
        imageFiles.forEach(f => fd.append('files', f))
        await uploadRepairImages(res.data.id, fd)
      }
      notifications.show({ color: 'teal', message: t('repair.createdSuccessMsg', 'Repair job created successfully') })
      reset(); onClose(); onSuccess(res.data)
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('repair.createErrorMsg', 'Error creating repair job') })
    } finally { setSaving(false) }
  }

  const handleClose = () => { reset(); onClose() }
  const assetOptions = repairAssets.map((a: any) => ({
    value: String(a.id),
    label: `${a.assetCode} — ${a.name} (S/N: ${a.serialNumber ?? '-'})`,
  }))

  // ── asset label for display ──
  const assetLabel = assetOptions.find(a => a.value === String(selectedAssetId ?? preselectedAssetId))?.label
    ?? (preselectedAssetId ? `Asset #${preselectedAssetId}` : null)

  return (
    <Modal opened={opened} onClose={handleClose} size={isSimple ? 'md' : 'lg'} radius="lg"
      title={
        <Group gap={8}>
          <IconTool size={16} />
          <Text fw={700} style={{ fontFamily: 'Inter,sans-serif', fontSize: 16 }}>
            {isSimple ? t('repair.sendToRepairTitle', 'Send to Repair') : t('repair.newRepairJobTitle', 'New Repair Job')}
          </Text>
        </Group>
      }>
      <Stack gap="sm">

        {/* ── Asset selector ── */}
        {!preselectedAssetId ? (
          <Select label={t('repair.assetLabel', 'Asset *')} searchable clearable placeholder={t('repair.selectAssetPlaceholder', 'Select asset...')}
            data={assetOptions} value={selectedAssetId} onChange={setSelectedAssetId}
            description={assetOptions.length === 0
              ? t('repair.noAvailableAssets', '⚠ No available assets')
              : t('repair.assetsAvailable', '{{count}} asset(s) available', { count: assetOptions.length })} />
        ) : (
          <Box p={10} style={{ background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
            <Group gap={8}>
              <Text size="xs" c="dimmed">{t('repair.assetColon', 'Asset:')}</Text>
              <Text size="sm" fw={600}>{assetLabel}</Text>
            </Group>
          </Box>
        )}

        {/* ══ SIMPLE MODE ════════════════════════════════════════════════════ */}
        {isSimple && (<>
          <Textarea label={t('repair.problemLabel', 'Problem / Symptom *')} placeholder={t('repair.problemPlaceholder', 'Describe the issue in detail...')}
            minRows={3} value={form.problem} onChange={e => setForm(p => ({ ...p, problem: e.target.value }))} />

          <SmartDateInput
            label={<Group gap={4}><IconCalendar size={13} /><Text size="sm">{t('repair.sendDateRequired', 'Send Date *')}</Text></Group>}
            placeholder={t('license.datePlaceholder', 'DD/MM/YYYY')}
            value={form.sentDate} onChange={v => setForm(p => ({ ...p, sentDate: v }))} clearable
          />

          <Box p={10} style={{ background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
            <Text size="xs" c="orange.7">
              <strong>{t('common.note', 'Note')}:</strong> {t('repair.statusChangeNotePrefix', 'Asset status will change to')} <Badge size="xs" color="orange">{t('repair.inRepairBadge', 'IN REPAIR')}</Badge>.
              {' '}{t('repair.statusChangeNoteSuffix', 'You can set Repair Type, Vendor, and other details in the Repair page.')}
            </Text>
          </Box>

          <TextInput label={t('repair.noteOptional', 'Note (optional)')} placeholder={t('repair.additionalNotesPlaceholder', 'Additional notes...')}
            value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
        </>)}

        {/* ══ FULL MODE ══════════════════════════════════════════════════════ */}
        {!isSimple && (<>
          <Select label={t('repairModal.repairType', 'ประเภทการซ่อม')} value={jobType}
            onChange={v => { setJobType(v ?? 'INTERNAL'); setInternalRepairType('GENERAL'); setConsumableUsed([]); setAssetUsedId(null) }}
            data={[
              { value: 'EXTERNAL', label: '🏢 ' + t('repairModal.typeExternal', 'ส่งซ่อมนอก (ส่งให้ผู้ขาย/ร้านซ่อม)') },
              { value: 'INTERNAL', label: '🔧 ' + t('repairModal.typeInternal', 'ซ่อมภายใน') },
              { value: 'ON_SITE',  label: '🛠️ ' + t('repairModal.typeOnSite', 'ช่างมาที่หน้างาน') },
            ]} />

          <Textarea label={t('repair.problemLabel', 'Problem / Symptom *')} placeholder={t('repair.problemPlaceholder', 'Describe the issue in detail...')}
            minRows={3} value={form.problem} onChange={e => setForm(p => ({ ...p, problem: e.target.value }))} />

          {jobType === 'ON_SITE' && (
            <Box p={12} style={{ background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0' }}>
              <Text size="sm" fw={600} c="teal.7" mb={10}>{t('repair.onSiteServiceDetails', 'On Site Service Details')}</Text>
              <Stack gap="sm">
                <SimpleGrid cols={2}>
                  <TextInput label={t('repair.technicianName', 'Technician Name *')} value={form.technicianName} onChange={e => setForm(p => ({ ...p, technicianName: e.target.value }))} />
                  <TextInput label={t('repair.contactPhone', 'Contact / Phone')} value={form.technicianContact} onChange={e => setForm(p => ({ ...p, technicianContact: e.target.value }))} />
                </SimpleGrid>
                <SimpleGrid cols={2}>
                  <SmartDateInput label={t('repair.serviceDateRequired', 'Service Date *')} placeholder={t('license.datePlaceholder', 'DD/MM/YYYY')} value={form.serviceDate} onChange={v => setForm(p => ({ ...p, serviceDate: v }))} clearable />
                  <TextInput label={t('repair.serviceLocation', 'Service Location')} value={form.serviceLocation} onChange={e => setForm(p => ({ ...p, serviceLocation: e.target.value }))} />
                </SimpleGrid>
                <SimpleGrid cols={2}>
                  <TextInput label={t('repair.contractPoNo', 'Contract / PO No.')} value={form.serviceContractNo} onChange={e => setForm(p => ({ ...p, serviceContractNo: e.target.value }))} />
                  <NumberInput label={t('repair.estimatedCostThb', 'Estimated Cost (฿)')} min={0} decimalScale={2} value={form.estimatedCost} onChange={v => setForm(p => ({ ...p, estimatedCost: Number(v) || 0 }))} />
                </SimpleGrid>
                <Select label={t('repair.vendorProvider', 'Vendor / Repair Shop')} searchable clearable data={vendors.map((v: any) => ({ value: v.name, label: v.name }))} value={form.vendorName || null} onChange={v => setForm(p => ({ ...p, vendorName: v ?? '' }))} />
              </Stack>
            </Box>
          )}

          {jobType === 'EXTERNAL' && (<>
            <Select label={t('repair.vendorLabel', 'Vendor / Repair Shop')} searchable clearable placeholder={t('maintenance.vendorSelectPlaceholder', 'Select or type vendor...')}
              data={vendors.map((v: any) => ({ value: v.name, label: v.name }))}
              value={form.vendorName || null}
              onChange={v => setForm(p => ({ ...p, vendorName: v ?? '' }))}
              onSearchChange={q => { if (q) setForm(p => ({ ...p, vendorName: q })) }} />
            <Grid>
              <Grid.Col span={6}><SmartDateInput label={t('repair.sentDate', 'Sent Date')} placeholder={t('license.datePlaceholder', 'DD/MM/YYYY')} value={form.sentDate} onChange={v => setForm(p => ({ ...p, sentDate: v }))} clearable /></Grid.Col>
              <Grid.Col span={6}><SmartDateInput label={t('maintenance.expectedReturnDate', 'Expected Return Date')} placeholder={t('license.datePlaceholder', 'DD/MM/YYYY')} value={form.expectedReturnDate} onChange={v => setForm(p => ({ ...p, expectedReturnDate: v }))} clearable /></Grid.Col>
            </Grid>
            <NumberInput label={t('repair.estimatedCostExclVat', 'Estimated Cost (THB, Excl. VAT)')} min={0} decimalScale={2} value={form.estimatedCost} onChange={v => setForm(p => ({ ...p, estimatedCost: Number(v) || 0 }))} />
          </>)}

          {jobType === 'INTERNAL' && (
            <Box style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 14px' }}>
              <Text size="sm" fw={600} mb={8}>{t('repair.internalMethod', 'Internal Repair Method')}</Text>
              <Select value={internalRepairType}
                onChange={v => { setInternalRepairType(v ?? 'GENERAL'); setConsumableUsed([]); setAssetUsedId(null) }}
                data={[
                  { value: 'GENERAL', label: '🔧 ' + t('repair.methodGeneral', 'General Repair (no parts used)') },
                  { value: 'USE_CONSUMABLE', label: '📦 ' + t('repair.methodConsumable', 'Use Consumable / Spare Parts') },
                  { value: 'USE_ASSET', label: '💻 ' + t('repair.methodAsset', 'Use Another Asset as Replacement Part') },
                ]} mb="sm" />
              {internalRepairType === 'USE_CONSUMABLE' && (
                <Box>
                  <Text size="xs" c="dimmed" mb={6}>{t('repair.selectConsumablesUsed', 'Select consumable items used in this repair')}</Text>
                  {consumableUsed.map((cu, i) => (
                    <Box key={i} mb={8} p={10} style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                      <Group gap="sm" align="flex-end">
                        <Select style={{ flex: 1 }} size="xs" placeholder={t('repair.selectConsumablePlaceholder', 'Select consumable...')} searchable
                          data={consumableItems.map((c: any) => ({ value: String(c.id), label: `${c.name} (${c.stockQty ?? 0} ${c.unit ?? 'PCS'})` }))}
                          value={cu.id ? String(cu.id) : null}
                          onChange={v => {
                            const found = consumableItems.find((c: any) => String(c.id) === v)
                            if (found) setConsumableUsed(p => p.map((x, j) => j === i ? { id: found.id, name: found.name, qty: x.qty } : x))
                          }} />
                        <NumberInput size="xs" style={{ width: 90 }} label={t('common.qty', 'Qty')} min={1} value={cu.qty} onChange={v => setConsumableUsed(p => p.map((x, j) => j === i ? { ...x, qty: Number(v) || 1 } : x))} />
                        <ActionIcon color="red" variant="light" size="sm" mb={1} onClick={() => setConsumableUsed(p => p.filter((_, j) => j !== i))}><IconX size={12} /></ActionIcon>
                      </Group>
                    </Box>
                  ))}
                  <Button size="xs" variant="light" color="teal" leftSection={<IconPlus size={12} />} onClick={() => setConsumableUsed(p => [...p, { id: 0, name: '', qty: 1 }])}>{t('repair.addConsumable', 'Add Consumable')}</Button>
                </Box>
              )}
              {internalRepairType === 'USE_ASSET' && (
                <Select label={t('repair.assetUsedAsPart', 'Asset used as replacement part')} placeholder={t('repair.selectAvailableAssetPlaceholder', 'Select available asset...')} searchable clearable
                  data={allAssets.map((a: any) => ({ value: String(a.id), label: `${a.assetCode} — ${a.name}` }))}
                  value={assetUsedId} onChange={setAssetUsedId} />
              )}
            </Box>
          )}

          <TextInput label={t('repair.note', 'Note')} placeholder={t('repair.additionalNotesPlaceholder', 'Additional notes...')} value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
        </>)}

        {/* ── Photos (both modes) ── */}
        <Divider />
        <Box>
          <Text size="sm" fw={500} mb={6}>{t('repair.attachPhotos', 'Attach Equipment Photos')}</Text>
          <Group gap="sm" wrap="wrap">
            {imagePreviews.map((src, i) => (
              <Box key={i} style={{ position: 'relative' }}>
                <Image src={src} w={72} h={72} radius="md" fit="cover" style={{ border: '1px solid #e2e8f0' }} />
                <ActionIcon size="xs" color="red" variant="filled" radius="xl" style={{ position: 'absolute', top: -4, right: -4 }}
                  onClick={() => { setImageFiles(p => p.filter((_, j) => j !== i)); setImagePreviews(p => p.filter((_, j) => j !== i)) }}>
                  <IconX size={10} />
                </ActionIcon>
              </Box>
            ))}
            <FileButton onChange={fs => addImages(fs)} accept="image/*" multiple>
              {(props) => <Button {...props} variant="outline" color="gray" size="xs" leftSection={<IconUpload size={12} />}>{t('repair.addPhotos', 'Add Photos')}</Button>}
            </FileButton>
          </Group>
        </Box>

        {/* ── Video Links ── */}
        {!isSimple && (
          <Box>
            <Group gap={6} mb={8}>
              <IconVideo size={14} color="#64748b" />
              <Text size="sm" fw={500} c="dimmed">{t('repair.videoLinks', 'Video Links (YouTube / Google Drive / Vimeo)')}</Text>
            </Group>
            <Group gap={8} mb={8}>
              <TextInput
                placeholder={t('repair.pasteVideoUrlPlaceholder', 'Paste YouTube, Google Drive or Vimeo URL...')}
                value={videoInput}
                onChange={e => setVideoInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && videoInput.trim()) {
                    e.preventDefault()
                    if (!videoUrls.includes(videoInput.trim())) {
                      setVideoUrls(prev => [...prev, videoInput.trim()])
                    }
                    setVideoInput('')
                  }
                }}
                style={{ flex: 1 }}
                size="sm"
              />
              <Button size="sm" variant="light" color="teal"
                disabled={!videoInput.trim()}
                onClick={() => {
                  if (videoInput.trim() && !videoUrls.includes(videoInput.trim())) {
                    setVideoUrls(prev => [...prev, videoInput.trim()])
                  }
                  setVideoInput('')
                }}>
                {t('common.add', 'Add')}
              </Button>
            </Group>
            {videoUrls.length > 0 && (
              <Stack gap={6}>
                {videoUrls.map((url, i) => {
                  const embed = getVideoEmbed(url)
                  return (
                    <Box key={i} style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                      <Group justify="space-between" px={10} py={6}>
                        <Anchor href={url} target="_blank" size="xs" c="teal">
                          {getVideoLabel(url)} #{i + 1}
                        </Anchor>
                        <ActionIcon size="xs" variant="subtle" color="red"
                          onClick={() => setVideoUrls(prev => prev.filter((_, idx) => idx !== i))}>
                          <IconX size={11} />
                        </ActionIcon>
                      </Group>
                      {embed && (
                        <Box style={{ position: 'relative', paddingTop: '42%' }}>
                          <iframe
                            src={embed}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Stack>
            )}
          </Box>
        )}

        <Group justify="flex-end" pt="xs" style={{ borderTop: '1px solid #f1f5f9' }}>
          <Button variant="default" onClick={handleClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button loading={saving} onClick={handleCreate}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
            {isSimple ? t('repair.confirmSendToRepair', 'Confirm — Send to Repair') : t('repair.createRepairJob', 'Create Repair Job')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
